import { logger } from '../../utils/logger';
import { getEnv } from '../../config/env';
import { authenticatedFetch } from '../../utils/authenticatedFetch';
import { getV2AuthHeaders } from '../../utils/authHeaders';

export interface DownloadMigrationResultsOptions {
  stage?: string;
}

export class NoResultsAvailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NoResultsAvailableError';
  }
}

/**
 * Downloads migration results as a ZIP file from the backend
 * @param accessToken - Bearer token for authentication
 * @param migrationId - ID of the migration to download results for
 * @param options - Optional parameters like stage filter
 * @throws {NoResultsAvailableError} When no results are available for the migration
 */
export async function downloadMigrationResults(
  accessToken: string,
  migrationId: string,
  options?: DownloadMigrationResultsOptions
): Promise<void> {
  const env = getEnv();
  const baseUrl = env.VITE_EDCI_API_URL.replace(/\/$/, '');

  // Build query string if stage is provided
  const queryParams = new URLSearchParams();
  if (options?.stage) {
    queryParams.append('stage', options.stage);
  }
  const queryString = queryParams.toString();
  const url = `${baseUrl}/api/migrations/${migrationId}/download${queryString ? `?${queryString}` : ''}`;

  logger.info('Downloading migration results', { migrationId, stage: options?.stage });

  try {
    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: getV2AuthHeaders(accessToken)
    });

    if (!response.ok) {
      // Check if it's a 404 (no results available)
      if (response.status === 404) {
        logger.warn('No results available for migration', { migrationId });
        throw new NoResultsAvailableError('No results are available for this migration');
      }

      logger.error('Failed to download migration results', {
        migrationId,
        status: response.status,
        statusText: response.statusText
      });
      throw new Error(`Failed to download migration results: ${response.statusText}`);
    }

    // Get the blob from the response
    const blob = await response.blob();

    // Extract filename from Content-Disposition header or use a default
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `migration-${migrationId}-results.zip`;

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch?.[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    // Create a temporary link element and trigger the download
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    // Clean up
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);

    logger.info('Migration results downloaded successfully', { migrationId, filename });
  } catch (error) {
    logger.error('Error downloading migration results', { migrationId, error });
    throw error;
  }
}
